import react, { useState, useContext, useEffect, useMemo, useReducer } from 'react'

import { Box, ListItemButton, Tooltip, List, ListItem, ListSubheader, InputLabel, TextField, Button, IconButton } from '@mui/material'
import { ScreenContext } from '..'
import { lockedReducer } from '../../../utils/defaultReducer'
import AvatarName from '../../AvatarName'
import ChangeField from '../../ChangeField'
import dayjs from 'dayjs'
import './styles.css'
import useNotification from '../../Notification'
import api from '../../../api'
import { getSession } from '../../../auth'
import { GeneralData } from './aux_components'

import { WhatsApp, Sms, Email, Phone, Edit, Add, Event as EventIcon } from '@mui/icons-material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import NumbersOutlinedIcon from '@mui/icons-material/NumbersOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import ListIcon from '@mui/icons-material/List';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CelebrationIcon from '@mui/icons-material/Celebration';
import SpeakerNotesOffIcon from '@mui/icons-material/SpeakerNotesOff';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import WorkIcon from '@mui/icons-material/Work';
import KeyIcon from '@mui/icons-material/Key';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

import ProfileTab from './profiletab'

import { useTelephony } from '../../../contexts/TelephonyContext'
import Templates from '../Templates'
import SendEmail from '../TabButtons/SendMessage/SendEmail'
import SendSMS from '../TabButtons/SendMessage/SendSMS'
import GroupAutocomplete from '../../Autocomplete/Group'
import GroupBadge from '../../Groups/GroupBadge'
import OutsideClickHandler from 'react-outside-click-handler'
import userPermissions from "../../User/Permissions"
import Event from "../../Agenda/Event"
import { OpportunityByContact } from '../../Journeys/OpportunityByContact'
import ContactAvatarProfile from './avatar'

const userGroups = userPermissions()

const customFieldHash = {
    'string': <NotesOutlinedIcon />,
    'integer': <NumbersOutlinedIcon />,
    'boolean': <RuleOutlinedIcon />,
    'select': <ListIcon />,
    'date': <EventIcon />,
    'textarea': <NotesOutlinedIcon />,
}

const normalizeOwnerOption = (value = '') => value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()

const formatOwnerName = (user) => `${user.first_name || ''} ${user.last_name || ''}`.trim()

const createOwnerOptionEntries = (usersEmails = []) => {
    const collator = new Intl.Collator('pt-BR', { sensitivity: 'base' })

    const sortedUsers = [...usersEmails]
        .filter((user) => normalizeOwnerOption(formatOwnerName(user) || user.username || '') !== 'publico')
        .sort((firstUser, secondUser) => {
            const firstLabel = formatOwnerName(firstUser) || firstUser.username || ''
            const secondLabel = formatOwnerName(secondUser) || secondUser.username || ''

            return collator.compare(firstLabel, secondLabel)
        })

    return [
        ['', 'Público'],
        ...sortedUsers.map((user) => [user.id, formatOwnerName(user) || user.username || ''])
    ]
}

const generateDate = (value) => {
    const date = dayjs(value).utcOffset(-6).locale('pt-BR').format('MMMM DD, YYYY HH:mm')
    return date[0].toLocaleUpperCase() + date.slice(1, date.length)
}

export const copyToClipboard = (value) => {
    navigator.clipboard.writeText(value)
    useNotification(
        'Sucesso!',
        'Texto copiado',
        'success'
    )
}

const createChannels = (state, setState, canCreateOpportunity = null, callContact = null) => {
    return {
        'agenda': { name: 'Criar Evento', component: (<></>), icon: (<EventIcon sx={{ color: '#5166a6' }} />), show: () => { return getSession().profile?.enterprise?.show_agenda }, onClick: () => setState({ showEventDialog: true }) },
        'opportunity': { name: 'Criar Oportunidade', component: (<></>), icon: (<WorkIcon sx={{ color: '#5166a6' }} />),  show: () => { return canCreateOpportunity ? canCreateOpportunity() : true }, onClick: () => setState({ showCreateOpportunity: true }) },
        'whatsapp': { name: 'Whatsapp', component: (<></>), icon: (<WhatsApp sx={{ color: '#34af23' }} />),  show: (state) => {return state.telephone}, onClick: () => setState({ singleClientWhatsAppNotify: state.id }) },
        'sms': { name: 'SMS', component: (<></>), icon: (<Sms sx={{ color: '#757575' }} />),  show: (state) => {return state.telephone}, onClick: () => setState({ singleClientSmsNotify: [state.telephone] }) },
        'email': { name: 'E-Mail', component: (<></>), icon: (<Email sx={{ color: '#757575' }} />), show: (state) => {return state.email},  onClick: () => setState({ singleClientEmailNotify: state.id }) },
        'phone': { name: 'Ligação', component: (<></>), icon: (<Phone />), show: (state) => {return state.telephone}, onClick: () => callContact && callContact(state.telephone) }
    }
}

const loadContact = async (id, setState) => {
    setState({ loading: true })
    const enterpriseId = getSession()?.profile.enterprise.id
    const [contact] = await Promise.all([
        api.get(`api/enterprises/${enterpriseId}/contacts/${id}/`)
    ])
    let result = { loading: false }
    if (contact.status === 200)
        result = { ...result, ...contact.data }
    setState(result)
}

const updateGroups = async (profile, state, setState) => {
    if (!state.groups_changed) {
        setState({ show_change_groups: false })
        return
    }
    setState({ loading: true })
    const enterpriseId = getSession()?.profile.enterprise.id
    const res = await api.patch(`api/enterprises/${enterpriseId}/contacts/${profile.id}/`, { groups: state.groups.map(group => group.id) })
    let result = { loading: false, groups_changed: false, show_change_groups: false }
    if (res.status === 200) {
        useNotification(
            'Sucesso!',
            'Segmentos atualizados!',
            'success'
        )
        result.groups = res.data.groups
        profile.groups = result.groups
        if ('reloadChat' in window)
            window.reloadChat()
    }

    setState(result)
}

const ContactProfile = () => {

    const { profile, templates, queues, clients, originNumbers, customFields, usersEmails, myTeam, supervisedQueues } = useContext(ScreenContext)
    const { callContact } = useTelephony()

    const [state, setState] = useReducer(lockedReducer, {
        contact_enterprise_name: '',
        loading: false,
        notification_opt_out: false,
        custom_fields: [],
        groups: [],
        enterprise_groups: [],
        showCreateOpportunity: false,
        ...profile,
        singleClientWhatsAppNotify: null,
        singleClientSmsNotify: null,
        singleClientEmailNotify: null,
        show_change_groups: false,
        groups_changed: false,
        showEventDialog: false,
        setup: null
    })

    const [userJourneys, setUserJourneys] = useState(null)
    const permissions = userPermissions()

    useEffect(() => {
        const fetchUserJourneys = async () => {
            try {
                const enterpriseId = getSession().profile.enterprise.id
                const response = await api.get(`/api/enterprises/${enterpriseId}/journeys/`)
                if (response.status === 200) {
                    const journeysData = response.data.results || response.data
                    setUserJourneys(journeysData)
                }
            } catch (error) {
                setUserJourneys([])
            }
        }
        fetchUserJourneys()
    }, [])

    const canCreateOpportunity = () => {
        if (!getSession()?.profile?.enterprise?.show_journey) {
            return false
        }
        // if (permissions.isOperator && !permissions.isNotOnlyOperator) {
        //     return userJourneys && userJourneys.length > 0
        // }
        return true
    }

    const [whatsAppNotifyData, setWhatsAppNotifyData] = useState({
            show: false,
            templates: [],
            queues: {},
            clients: [],
            selectionIds: [],
            originNumbers: []
        })
        const [smsNotifyData, setSMSNotifyData] = useState({
            show: false,
            queues: [],
            telephone: []
        })
        const [emailNotifyData, setEmailNotifyData] = useState({
            show: false,
            queues: [],
            clients: []
        })

    const channels = useMemo(() => createChannels(state, setState, canCreateOpportunity, callContact), [state, userJourneys, callContact])
    const custom_field_hash = useMemo(() => Object.fromEntries(state.custom_fields.map(field => ([field.custom_field.id, field]))))

    useEffect(() => {
        loadContact(profile.id, setState)
    }, [profile])

    const ownerOptionEntries = useMemo(() => createOwnerOptionEntries(usersEmails), [usersEmails])
    const users = useMemo(() => Object.fromEntries(ownerOptionEntries), [ownerOptionEntries])

    const createEvent = async (data) => {
        const response = await api.post(`api/enterprises/${getSession().profile.enterprise.id}/tasks/`, {
            ...data,
            target_date: dayjs(data.target_date).format("YYYY-MM-DDTHH:mm"),
            contact: (data.contact) ? data.contact.id : null,
            contact_enterprise: (data.contact_enterprise) ? data.contact_enterprise.id : null,
            owner: getSession().id,
            status: "SCHEDULED",
            target: (! data.target) ? getSession().id : data.target,
            is_recurrent: (data.is_recurrent === "UNIQUE") ? "" : data.is_recurrent
        })
        if (response.status === 201) {
            setState({ showEventDialog: false })
            await useNotification(
                "Sucesso",
                "Tarefa criada com sucesso.",
                "success"
            )
            return
        }
        await useNotification(
            "Ops",
            "Algo deu errado, tente novamente.",
            "danger"
        )
    }

    const setupWhatsAppNotify = async (contact, setup) => {
        const templates = templates
        const queues = queues
        const clients = state
        const selectionIds = [state.id]
        const originNumbers = originNumbers
        setWhatsAppNotifyData((prevState) => ({
            ...prevState,
            show: true,
            templates: templates,
            queues: queues,
            clients: clients,
            selectionIds: selectionIds,
            originNumbers: originNumbers,
            setup: setup
        }))
    }

    const setupSMSNotify = async (contact, setup) => {
        const queues = queues
        setSMSNotifyData((prevState) => ({
            ...prevState,
            show: true,
            queues: queues,
            telephone: [state.telephone],
            setup: setup
        }))
    }

    const setupEmailNotify = async (contact, setup) => {
        const queues = queues
        const clients = [state.id]
        setEmailNotifyData((prevState) => ({
            ...prevState,
            show: true,
            queues: queues,
            clients: clients,
            setup: setup
        }))
    }

    return (
        <>
            <Box className='contactprofile-mainframe'>
                <Box className='contactprofile-left'>
                    <ContactAvatarProfile parent={state} />
                    <span className='contact-name'>
                        <GeneralData icon={<></>} name={''} data={state} editable='name' onClick={() => copyToClipboard(state.telephone)} reloadParent={() => setState({ ...state })} placeholder="Nome" />
                    </span >
                    <Box sx={{ fontSize: '.9em' }}>
                        {generateDate(state.created_at)}
                    </Box>
                    <List className='contact-channels' disablePadding>
                        {Object.entries(channels).map(([key, value]) => (
                            <Tooltip title={value.name} disabled={'show' in value && !value.show(state)}>
                                <ListItemButton disablePadding onClick={() => { 'onClick' in value ? value.onClick() : null }}>
                                    <ListItem disablePadding>
                                        {value.icon}
                                    </ListItem>
                                </ListItemButton>
                            </Tooltip>
                        ))}
                    </List>
                    <List className='contact-items' disablePadding>
                        {/* <GeneralData  icon={<InfoOutlinedIcon />} name={'Status'} data={state.active} /> */}
                        <GeneralData
                            icon={<MapsHomeWorkOutlinedIcon />}
                            name={'Empresa'}
                            placeholder={'Empresa'}
                            data={state}
                            editable='contact_enterprise'
                            namealias={state.contact_enterprise_name}
                            onClick={() => copyToClipboard(state.contact_enterprise_name)}
                            reloadParent={() => setState({ ...state })}
                        />
                        <GeneralData
                            icon={<SupportAgentIcon />}
                            name={'Dono'}
                            placeholder={'Público'}
                            data={state}
                            editable='owner'
                            namealias={state.owner_name}
                            options={users}
                            optionEntries={ownerOptionEntries}
                            onClick={() => copyToClipboard(state.owner_name)}
                            reloadParent={() => setState({ ...state })}
                        />
                        <GeneralData icon={<CallOutlinedIcon />} name={'Telefone'} placeholder={'Telefone'} data={state} editable='telephone' onClick={() => copyToClipboard(state.telephone)} reloadParent={() => setState({ ...state })} />
                        <GeneralData icon={<EmailOutlinedIcon />} name={'E-mail'} data={state} placeholder={'E-mail'} editable='email' onClick={() => copyToClipboard(state.email)} reloadParent={() => setState({ ...state })} />
                        {/* <GeneralData  icon={<LocationOnOutlinedIcon />} name={'Endereço'} data={'Somewhere'} editable onClick={() => copyToClipboard(state.telephone)} reloadParent={()=>setState({...state})}/> */}
                        <GeneralData icon={<SendOutlinedIcon />} name={'Recebe Campanhas'} data={state} placeholder={'Sim/Não'} editable='notification_opt_out' options={{ 'false': 'Sim', 'true': 'Não' }} reloadParent={() => setState({ ...state })} />
                        <GeneralData icon={<SpeakerNotesOffIcon />} name={'Não perturbar'} data={state} placeholder={'Sim/Não'} editable='no_disturb' options={{ 'true': 'Sim', 'false': 'Não' }} reloadParent={() => setState({ ...state })} />
                        <GeneralData icon={<CelebrationIcon />} date={true} name={'Aniversário'} data={state} placeholder={'DD/MM/YYYY'} editable='birthday' reloadParent={() => setState({ ...state })} />
                        <GeneralData icon={<AssignmentIndIcon />} cpf={true} name={'CPF'} data={state} placeholder={'000.000.000-00'} editable='cpf' reloadParent={() => setState({ ...state })} />
                        <GeneralData icon={<KeyIcon />} name={'Código'} placeholder={'Código'} data={state} editable='customer_code' onClick={() => copyToClipboard(state.customer_code)} reloadParent={() => setState({ ...state })} />
                        <GeneralData icon={<LinkedInIcon />} name={'LinkedIn'} placeholder={'LinkedIn'} data={state} editable='linkedin_url' reloadParent={() => setState({ ...state })} />

                        <ListSubheader
                            disableSticky
                            sx={{
                                bgcolor: "transparent",
                                fontSize: ".8rem",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                color: "text.secondary",
                            }}
                        >
                            Segmentos
                            {!state.show_change_groups && 
                                <IconButton 
                                    onClick={() => setState({ show_change_groups: true })} 
                                    sx={{ 
                                        padding: ".25rem",
                                        fontSize: ".8em",
                                        marginLeft: ".25rem"
                                    }}
                                >
                                    <Edit sx={{ fontSize: "2em" }} />
                                </IconButton>
                            }
                        </ListSubheader>

                        {state.show_change_groups 
                            ? (
                                <OutsideClickHandler onOutsideClick={() => {
                                    updateGroups(profile, state, setState)
                                }}>
                                    <GroupAutocomplete
                                        sx={{ px: 2 }}
                                        selectedGroup={[
                                            ...new Map(
                                                [
                                                    ...state.groups.map(g => ({ ...g, isEnterprise: false })),
                                                    ...state.enterprise_groups.map(g => ({ ...g, isEnterprise: true }))
                                                ].map(item => [item.id, item])
                                            ).values()
                                        ]}
                                        onChange={(data) => {
                                            const entIds = state.enterprise_groups.map(g=>g.id);
                                            let newGroups = data || [];
                                            entIds.forEach(id=>{
                                                if(!newGroups.find(g=>g.id===id)){
                                                    const ent = state.enterprise_groups.find(g=>g.id===id);
                                                    if(ent) newGroups.push(ent);
                                                }
                                            });
                                            const contactGroups = newGroups.filter(g=>!entIds.includes(g.id));
                                            setState({ 
                                                groups: contactGroups, 
                                                groups_changed: true
                                            })
                                        }} 
                                        disablePortal={true} 
                                        multiple={true} 
                                    />
                                </OutsideClickHandler>
                            )
                            : (
                                <Box className='contact-groups-list'>
                                    {[
                                        ...new Map(
                                            [...state.groups, ...state.enterprise_groups].map(item => [item.id, item])
                                        ).values()
                                    ].map(({ name, id }) => {
                                        const isEnterprise = state.enterprise_groups.some(e=>e.id===id);
                                        return <GroupBadge className="client-group-item" key={id} label={name} isEnterprise={isEnterprise} />
                                    })}
                                </Box>
                            )
                        }

                        <ListSubheader
                            disableSticky
                            sx={{
                                bgcolor: "transparent",
                                fontSize: ".8rem",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                color: "text.secondary",
                            }}
                        >
                            Campos Customizados
                        </ListSubheader>

                        {customFields.map(field => (
                            <GeneralData icon={customFieldHash[field.field_type]}
                                name={field.field_name}
                                data={{ ...custom_field_hash[field.id], profileId: state.id, custom_field: field }}
                                editable={'value'}
                                placeholder={'Valor'}
                                onClick={() => copyToClipboard(state.telephone)}
                                reloadParent={() => setState({ ...state })}
                                customField={state.custom_fields} />
                        ))}
                    </List>
                </Box>
                <Box className='contactprofile-right'>
                    <ProfileTab contact={profile} supervisedQueues={supervisedQueues} />
                </Box>
            </Box>
            {state.showEventDialog &&
                <Event 
                    open={state.showEventDialog}
                    title="Criar evento"
                    handleClose={() => setState({ showEventDialog: false })}
                    handleSubmit={createEvent}
                    isSupervisor={userGroups.isSupervisor}
                    myTeam={myTeam}
                    selectedEvent={{ contact_data: state }}
                    setProfile={() => {}}
                    setupWhatsAppNotify={(_, setup) => setState({ singleClientWhatsAppNotify: state.id, setup: setup })}
                    setupSMSNotify={(_, setup) => setState({ singleClientSmsNotify: [state.telephone], setup: setup })}
                    setupEmailNotify={(_, setup) => setState({ singleClientEmailNotify: state.id, setup: setup }) }
                    lock
                />
            }

            {state.singleClientWhatsAppNotify &&
                <Templates
                    title={state.setup ? "Configurar notificação de whatsapp" : "Enviar whatsapp ao contato"}
                    open={true}
                    templates={(getSession().profile.enterprise.abstraction_layer) ? [] : templates}
                    queues={queues}
                    clients={[state]}
                    selectionIds={[state.singleClientWhatsAppNotify]}
                    originNumbers={originNumbers}
                    handleClose={() => setState({ singleClientWhatsAppNotify: null, setup:null })}
                    handlePostSuccess={() => { }}
                    positionFixed
                    setup={state.setup}
                />
            }

            {state.singleClientEmailNotify &&
                <SendEmail
                    title={state.setup ? "Configurar notificação de e-mail" : "Enviar e-mail ao contato"}
                    queues={queues}
                    clients={[state.singleClientEmailNotify]}
                    handleClose={() => setState({ singleClientEmailNotify: null, setup:null })}
                    positionFixed
                    setup={state.setup}
                    canCopy={true}
                />
            }

            {state.singleClientSmsNotify &&
                <SendSMS
                    queues={queues}
                    selectedNumberModel={state.singleClientSmsNotify}
                    handleClose={() => setState({ singleClientSmsNotify: null, setup:null })}
                    positionFixed
                    title={state.setup ? "Configurar notificação de sms" : "Enviar SMS ao contato"}
                    setup={state.setup}
                />}

            {state.showCreateOpportunity &&
                <OpportunityByContact
                    open={state.showCreateOpportunity}
                    contact={state}
                    onClose={() => setState({ showCreateOpportunity: false })}
                    enterpriseId={getSession()?.profile.enterprise.id}
                    usersEmails={usersEmails}
                />
            }

        </>
    )
}


export default ContactProfile
